/**
 * V2 Agent Thought Process Renderer
 * Renders agent thought process items in order with filesystem clustering
 */

import { Bot, ChevronDown, Loader2 } from "lucide-react";
import * as React from "react";
import { graphql, useFragment } from "react-relay";
import { cn } from "src/common/utils";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "src/components/ui/accordion";
import { BorderBeam } from "src/components/ui/border-beam";
import type { AgentThoughtProcess_formattedMessage$data } from "src/components/ui/chat/__generated__/AgentThoughtProcess_formattedMessage.graphql";
import { AgentPendingQuestionType, AgentQuestion } from "src/components/ui/chat/AgentQuestion";
import { Markdown } from "src/components/ui/Markdown";

import { ChecklistToolCallResult } from "./ChecklistToolCallResult";
import { SkillCluster } from "./SkillCluster";
import { V2ToolButton } from "./V2ToolButton";
import { getThoughtProcessItemDisplayText, getV2ToolDisplayName, isFilesystemTool, isSkillTool, isToolButtonDisabled, shouldShowTool } from "./v2Utils";

// Fragment for tool button (when we need to render the button)
const toolCallFragment = graphql`
  fragment V2AgentThoughtProcessRenderer_toolCall on GraphQLMessageToolCall {
    toolUse {
      name
    }
    ...V2ToolButton_toolCall
  }
`;

type ThoughtProcessItem = AgentThoughtProcess_formattedMessage$data["agentThoughtProcess"][number];

// Component that calls useFragment for a single MCP tool button
const MCPToolButton: React.FC<{
  toolUseRef: any;
  toolName: string;
  displayText: string | null;
  shouldBlink: boolean;
  isFirstInList?: boolean;
  onClick?: () => void;
  isPending?: boolean;
  isWaiting?: boolean;
  onApprove?: () => void;
  onReject?: () => void;
  approvalInFlight?: boolean;
}> = React.memo(function MCPToolButton({
  toolUseRef,
  toolName,
  displayText,
  shouldBlink,
  isFirstInList = false,
  onClick,
  isPending = false,
  isWaiting = false,
  onApprove,
  onReject,
  approvalInFlight = false,
}) {
  // Only call useFragment once per component instance (stable)
  const toolData = useFragment(toolCallFragment, toolUseRef);

  return (
    <div className={cn("flex flex-col gap-1", isFirstInList ? "mt-2" : "mt-3")}>
      {displayText && (
        <div className="flex gap-2 items-start">
          <span className={cn("text-muted-foreground flex-shrink-0", shouldBlink && "animate-pulse")}>
            ●
          </span>
          <div className="text-foreground flex-1">
            <Markdown>{displayText}</Markdown>
          </div>
        </div>
      )}

      <div className={cn("flex gap-2 items-start", !displayText && "mt-0")}>
        <span className="text-muted-foreground flex-shrink-0 invisible">●</span>
        <V2ToolButton
          toolCall={toolData}
          onClick={onClick}
          isDisabled={isToolButtonDisabled(toolName)}
          isPending={isPending}
          isWaiting={isWaiting}
          onApprove={onApprove}
          onReject={onReject}
          approvalInFlight={approvalInFlight}
        />
      </div>
    </div>
  );
});

// Wrapper component that manages a group of tools
const ToolGroupRenderer: React.FC<{
  items: ReadonlyArray<ThoughtProcessItem>;
  indices: number[];
  shouldBlink: boolean;
  isFirstInRenderGroups?: boolean;
  onToolClick?: (index: number) => void;
  pendingToolUses?: ReadonlyArray<PendingToolUse>;
  onApproveToolUse?: (toolUseId: string) => void;
  onRejectToolUse?: (toolUseId: string) => void;
  approvalInFlight?: boolean;
  pendingQuestions?: AgentPendingQuestionType[];
  onQuestionSubmit?: (toolUseId: string, answers: any[]) => void;
  isSubmittingQuestion?: boolean;
}> = React.memo(function ToolGroupRenderer({
  items,
  indices,
  shouldBlink,
  isFirstInRenderGroups = false,
  onToolClick,
  pendingToolUses = [],
  onApproveToolUse,
  onRejectToolUse,
  approvalInFlight = false,
  pendingQuestions = [],
  onQuestionSubmit,
  isSubmittingQuestion = false,
}) {
  // Get tool data directly from items (no hooks in loops)
  const toolsData = indices.map((idx) => {
    const item = items[idx];
    return { item, index: idx };
  });

  return (
    <ToolOrClusterItem
      toolsData={toolsData}
      shouldBlink={shouldBlink}
      isFirstInRenderGroups={isFirstInRenderGroups}
      onToolClick={onToolClick}
      pendingToolUses={pendingToolUses}
      onApproveToolUse={onApproveToolUse}
      onRejectToolUse={onRejectToolUse}
      approvalInFlight={approvalInFlight}
      pendingQuestions={pendingQuestions}
      onQuestionSubmit={onQuestionSubmit}
      isSubmittingQuestion={isSubmittingQuestion}
    />
  );
});

// Component to render tools - uses direct field access to avoid hooks violations
// Maintains chronological order while clustering consecutive filesystem tools
const ToolOrClusterItem = React.memo<{
  toolsData: Array<{
    item: ThoughtProcessItem;
    index: number;
  }>;
  shouldBlink: boolean;
  isFirstInRenderGroups?: boolean;
  onToolClick?: (index: number) => void;
  pendingToolUses?: ReadonlyArray<PendingToolUse>;
  onApproveToolUse?: (toolUseId: string) => void;
  onRejectToolUse?: (toolUseId: string) => void;
  approvalInFlight?: boolean;
  pendingQuestions?: AgentPendingQuestionType[];
  onQuestionSubmit?: (toolUseId: string, answers: any[]) => void;
  isSubmittingQuestion?: boolean;
}>(function ToolOrClusterItem({
  toolsData,
  shouldBlink,
  isFirstInRenderGroups = false,
  onToolClick,
  pendingToolUses = [],
  onApproveToolUse,
  onRejectToolUse,
  approvalInFlight = false,
  pendingQuestions = [],
  onQuestionSubmit,
  isSubmittingQuestion = false,
}) {
  // Filter to only valid tools (non-null with a name AND should be shown) using direct field access
  const validTools = toolsData.filter((t) => {
    const toolName = t.item.toolUse?.toolUse?.name;
    return toolName && shouldShowTool(toolName);
  });

  // Group consecutive tools of the same kind together while maintaining order.
  // Skill tools cluster separately from filesystem tools so their names stay visible.
  // Start a new cluster if text/thinking/thinking_summary is detected.
  const toolGroups: Array<{
    type: "filesystem-cluster" | "skill-cluster" | "mcp";
    tools: typeof validTools;
  }> = [];

  let currentFsCluster: typeof validTools = [];
  let currentSkillCluster: typeof validTools = [];

  const flushFs = () => {
    if (currentFsCluster.length > 0) {
      toolGroups.push({ type: "filesystem-cluster", tools: currentFsCluster });
      currentFsCluster = [];
    }
  };
  const flushSkill = () => {
    if (currentSkillCluster.length > 0) {
      toolGroups.push({ type: "skill-cluster", tools: currentSkillCluster });
      currentSkillCluster = [];
    }
  };

  validTools.forEach((tool) => {
    const name = tool.item.toolUse!.toolUse!.name;
    const isFs = isFilesystemTool(name);
    const isSkill = isSkillTool(name);
    const hasText = getDisplayText(tool.item) !== null;

    if (isSkill) {
      // Skills don't mix with filesystem; flush any pending fs cluster first.
      flushFs();
      if (hasText && currentSkillCluster.length > 0) {
        flushSkill();
      }
      currentSkillCluster.push(tool);
    } else if (isFs) {
      flushSkill();
      if (hasText && currentFsCluster.length > 0) {
        flushFs();
      }
      currentFsCluster.push(tool);
    } else {
      flushSkill();
      flushFs();
      toolGroups.push({ type: "mcp", tools: [tool] });
    }
  });

  flushSkill();
  flushFs();

  // Check if this is the last tool group (for shimmer effect on cluster message)
  const lastGroupIndex = toolGroups.length - 1;

  return (
    <>
      {toolGroups.map((group, groupIdx) => {
        const isLastGroup = groupIdx === lastGroupIndex;

        if (group.type === "filesystem-cluster") {
          // Render filesystem tools cluster
          const displayTexts = group.tools
            .map((t) => getDisplayText(t.item))
            .filter((text): text is string => text !== null && text.trim() !== "");

          // Extract tool IDs for deterministic message selection
          const toolIds = group.tools
            .map((t) => t.item.toolUse?.toolUse?.id)
            .filter((id): id is string => id != null);

          // This cluster is "active" if it's the last group and shouldBlink is true
          const isActiveCluster = isLastGroup && shouldBlink;

          return (
            <div key={`fs-cluster-${groupIdx}`} className={cn("flex flex-col gap-1", groupIdx === 0 && isFirstInRenderGroups ? "mt-2" : "mt-3")}>
              {/* Show all text blocks from filesystem tools */}
              {displayTexts.map((text, idx) => (
                <div key={`fs-text-${idx}`} className="flex gap-2 items-start">
                  <span className="text-muted-foreground flex-shrink-0">
                    ●
                  </span>
                  <div className="text-foreground flex-1">
                    <Markdown>{text}</Markdown>
                  </div>
                </div>
              ))}

              {/* Show cluster summary */}
              <div className={cn("flex gap-2 items-start", displayTexts.length === 0 && "mt-0")}>
                <span className="text-muted-foreground flex-shrink-0 invisible">●</span>
                <div className={cn(
                  "text-sm text-muted-foreground italic",
                  isActiveCluster && "animate-pulse"
                )}>
                  {getDeterministicAnalysisMessage(group.tools.length, toolIds, isActiveCluster)}
                </div>
              </div>
            </div>
          );
        } else if (group.type === "skill-cluster") {
          // Render Skill invocations via SkillCluster — internally fetches the
          // org's CustomSkills, hides built-ins entirely, and lets each chip
          // open a slide-out sheet on click.
          return (
            <SkillCluster
              key={`skill-cluster-${groupIdx}`}
              tools={group.tools}
              shouldBlink={isLastGroup && shouldBlink}
            />
          );
        } else {
          // Render MCP tool individually
          const toolItem = group.tools[0];
          const toolName = toolItem.item.toolUse!.toolUse!.name;
          const itemDisplayText = getDisplayText(toolItem.item);

          // Special rendering for todo tools - render the checklist instead of a button
          if (toolName === "UpdateTodoTool" || toolName === "TodoWrite") {
            return (
              <div key={`todo-${groupIdx}`} className={groupIdx === 0 && isFirstInRenderGroups ? "mt-2" : "mt-3"}>
                {itemDisplayText && (
                  <div className="flex gap-2 items-start mb-2">
                    <span className="text-muted-foreground flex-shrink-0">●</span>
                    <div className="text-foreground flex-1">
                      <Markdown>{itemDisplayText}</Markdown>
                    </div>
                  </div>
                )}
                <ChecklistToolCallResult toolCall={toolItem.item.toolUse!} />
              </div>
            );
          }

          // Special rendering for AskUserQuestion - render the question UI inline
          if (toolName === "AskUserQuestion") {
            const toolUseId = toolItem.item.toolUse?.toolUse?.id;
            const pendingQuestion = toolUseId
              ? pendingQuestions.find((q) => q.toolUseId === toolUseId)
              : undefined;

            if (pendingQuestion) {
              const isTimedOut = pendingQuestion.status === "TIMEOUT";
              return (
                <div key={`question-${groupIdx}`} className={groupIdx === 0 && isFirstInRenderGroups ? "mt-2" : "mt-3"}>
                  {itemDisplayText && (
                    <div className="flex gap-2 items-start mb-2">
                      <span className="text-muted-foreground flex-shrink-0">●</span>
                      <div className="text-foreground flex-1">
                        <Markdown>{itemDisplayText}</Markdown>
                      </div>
                    </div>
                  )}
                  <AgentQuestion
                    question={pendingQuestion}
                    onSubmit={(answers) => onQuestionSubmit?.(toolUseId!, answers)}
                    isSubmitting={isSubmittingQuestion}
                    isTimedOut={isTimedOut}
                    embedded={true}
                  />
                </div>
              );
            }
            // If no pending question found, render nothing (question may not be created yet)
            return null;
          }

          // MCP tool should shimmer only if it's the last group
          const shouldShimmer = isLastGroup && shouldBlink;

          // Check if this tool has a pending approval request
          const toolUseId = toolItem.item.toolUse?.toolUse?.id;
          const pendingToolUse = toolUseId
            ? pendingToolUses.find((p) => p.toolUseId === toolUseId)
            : undefined;
          const isPending = pendingToolUse?.status === "PENDING";

          return (
            <MCPToolButton
              key={`mcp-${groupIdx}`}
              toolUseRef={toolItem.item.toolUse}
              toolName={toolName}
              displayText={itemDisplayText}
              shouldBlink={shouldShimmer}
              isFirstInList={groupIdx === 0 && isFirstInRenderGroups}
              onClick={onToolClick ? () => onToolClick(toolItem.index) : undefined}
              isPending={isPending}
              onApprove={
                isPending && toolUseId && onApproveToolUse
                  ? () => onApproveToolUse(toolUseId)
                  : undefined
              }
              onReject={
                isPending && toolUseId && onRejectToolUse
                  ? () => onRejectToolUse(toolUseId)
                  : undefined
              }
              approvalInFlight={approvalInFlight}
            />
          );
        }
      })}
    </>
  );
});

// Type for pending tool use approval status
type PendingToolUse = {
  toolUseId: string;
  status: string;
};

interface V2AgentThoughtProcessRendererProps {
  items: ReadonlyArray<ThoughtProcessItem>;
  isThinking?: boolean;
  startedAt?: string;
  endedAt?: string | null;
  onToolClick?: (index: number) => void;
  pendingToolUses?: ReadonlyArray<PendingToolUse>;
  onApproveToolUse?: (toolUseId: string) => void;
  onRejectToolUse?: (toolUseId: string) => void;
  approvalInFlight?: boolean;
  /** Whether this interaction has a timed out question - affects interrupt message display */
  hasTimedOutQuestion?: boolean;
  /** Pending questions for this interaction */
  pendingQuestions?: AgentPendingQuestionType[];
  /** Handler for submitting question answers */
  onQuestionSubmit?: (toolUseId: string, answers: any[]) => void;
  /** Whether a question answer is being submitted */
  isSubmittingQuestion?: boolean;
}

// Message templates for filesystem tool clusters (present and past tense)
const ANALYSIS_MESSAGE_TEMPLATES = [
  { present: "Running {count} analysis {tool_plural}", past: "Ran {count} analysis {tool_plural}" },
  { present: "Executing {count} {tool_plural}", past: "Executed {count} {tool_plural}" },
  { present: "Processing {count} {tool_plural}", past: "Processed {count} {tool_plural}" },
  { present: "Gathering information using {count} {tool_plural}", past: "Gathered information using {count} {tool_plural}" },
  { present: "Collecting data with {count} {tool_plural}", past: "Collected data with {count} {tool_plural}" },
  { present: "Reviewing information across {count} {tool_plural}", past: "Reviewed information across {count} {tool_plural}" },
  { present: "Inspecting details with {count} {tool_plural}", past: "Inspected details with {count} {tool_plural}" },
  { present: "Examining context using {count} {tool_plural}", past: "Examined context using {count} {tool_plural}" },
  { present: "Exploring details across {count} {tool_plural}", past: "Explored details across {count} {tool_plural}" },
  { present: "Investigating context with {count} {tool_plural}", past: "Investigated context with {count} {tool_plural}" },
];

// Thinking state messages (one-word only)
const THINKING_MESSAGES = [
  "Thinking...",
  "Processing...",
  "Analyzing...",
  "Considering...",
  "Evaluating...",
  "Reasoning...",
  "Pondering...",
  "Reflecting...",
  "Pontificating...",
  "Cogitating...",
  "Deliberating...",
  "Contemplating...",
  "Ruminating...",
  "Synthesizing...",
  "Deducing...",
  "Extrapolating...",
];

// Simple hash function for strings
function hashString(str: string): number {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash; // Convert to 32-bit integer
  }
  return Math.abs(hash);
}

// Helper function to get a deterministic analysis message based on tool IDs
function getDeterministicAnalysisMessage(count: number, toolIds: string[], isActive: boolean): string {
  // Create a stable string from the tool IDs for hashing
  const seedString = toolIds.sort().join("-");
  const hash = hashString(seedString);
  const index = hash % ANALYSIS_MESSAGE_TEMPLATES.length;
  const templatePair = ANALYSIS_MESSAGE_TEMPLATES[index];

  // Use present tense if active (current bottom message), past tense otherwise
  const template = isActive ? templatePair.present : templatePair.past;

  // Use proper pluralization
  const toolPlural = count === 1 ? "tool" : "tools";

  return template
    .replace("{count}", count.toString())
    .replace("{tool_plural}", toolPlural);
}

// Helper function to get display text with priority: thinking_summary > thinking
// Raw text blocks are intermediate agent output and shouldn't be shown as reasoning
// in the thought process panel. They're still fed to the thinking summary agent for
// summary generation, but not displayed directly.
const getDisplayText = getThoughtProcessItemDisplayText;

// Transform text to show timeout message instead of interrupt message when question timed out
function transformTextForTimeout(text: string | null, hasTimedOutQuestion: boolean): string | null {
  if (!text || !hasTimedOutQuestion) return text;
  return text.replace(INTERRUPT_MESSAGE_PATTERN, QUESTION_TIMEOUT_MESSAGE);
}

// Message to replace for question timeout
const INTERRUPT_MESSAGE_PATTERN = /\[Interrupted by User\][^\n]*/i;
const QUESTION_TIMEOUT_MESSAGE = "[Question timed out] The clarifying question was not answered in time.";

// Type for nested subagent items — derived from the actual GraphQL generated type
type SubagentItemType = NonNullable<ThoughtProcessItem["subagentItems"]>[number];

// Renders a single subagent item (text bullet or tool summary)
const SubagentItem = React.memo<{
  item: SubagentItemType;
  isLast: boolean;
  shouldBlink: boolean;
}>(function SubagentItem({ item, isLast, shouldBlink }) {
  const displayText = item.thinkingSummary?.trim() || item.text?.trim() || item.thinking?.trim() || null;
  const toolName = item.toolUse?.toolUse?.name;
  const isActive = isLast && shouldBlink;

  // Item has text content — show it, plus tool label if applicable
  if (displayText) {
    return (
      <div className="flex flex-col gap-1 mt-1">
        <div className="flex gap-2 items-start">
          <span className={cn("text-muted-foreground flex-shrink-0 text-xs", isActive && "animate-pulse")}>●</span>
          <div className="text-muted-foreground text-sm flex-1">
            <Markdown>{displayText}</Markdown>
          </div>
        </div>
      </div>
    );
  }

  // Tool-only item (no text) — show tool name
  if (toolName) {
    return (
      <div className="flex gap-2 items-start mt-1">
        <span className="text-muted-foreground flex-shrink-0 text-xs invisible">●</span>
        <span className={cn("text-muted-foreground text-xs italic", isActive && "animate-pulse")}>
          Used {toolName}
        </span>
      </div>
    );
  }

  return null;
});

// Groups consecutive tool-only subagent items into summary lines
const SubagentItemList = React.memo<{
  items: ReadonlyArray<SubagentItemType>;
  shouldBlink: boolean;
}>(function SubagentItemList({ items, shouldBlink }) {
  // Group consecutive tool-only items together, keep text items separate
  // Checklist tools (TodoWrite/UpdateTodoTool) get their own group type
  const groups: Array<
    | { type: "text"; item: SubagentItemType }
    | { type: "tools"; toolNames: string[] }
    | { type: "checklist"; item: SubagentItemType }
  > = [];

  let pendingTools: string[] = [];

  const flushTools = () => {
    if (pendingTools.length > 0) {
      groups.push({ type: "tools", toolNames: [...pendingTools] });
      pendingTools = [];
    }
  };

  // Pre-pass: find the last checklist tool index so we only render it once
  let lastChecklistIdx = -1;
  for (let i = items.length - 1; i >= 0; i--) {
    const toolName = items[i].toolUse?.toolUse?.name;
    if (toolName === "TodoWrite" || toolName === "UpdateTodoTool") {
      lastChecklistIdx = i;
      break;
    }
  }

  for (let i = 0; i < items.length; i++) {
    const item = items[i];
    const displayText = item.thinkingSummary?.trim() || item.text?.trim() || item.thinking?.trim() || null;
    const toolName = item.toolUse?.toolUse?.name;

    if (displayText) {
      flushTools();
      groups.push({ type: "text", item });
    } else if (toolName === "TodoWrite" || toolName === "UpdateTodoTool") {
      if (i === lastChecklistIdx) {
        flushTools();
        groups.push({ type: "checklist", item });
      }
    } else if (toolName) {
      pendingTools.push(toolName);
    }
  }
  flushTools();

  const lastGroupIdx = groups.length - 1;

  return (
    <>
      {groups.map((group, idx) => {
        const isLastGroup = idx === lastGroupIdx;
        const isActive = isLastGroup && shouldBlink;

        if (group.type === "text") {
          return (
            <SubagentItem
              key={`text-${idx}`}
              item={group.item}
              isLast={isLastGroup}
              shouldBlink={shouldBlink}
            />
          );
        }

        if (group.type === "checklist") {
          return (
            <div key={`checklist-${idx}`} className="mt-1">
              <ChecklistToolCallResult toolCall={group.item.toolUse!} />
            </div>
          );
        }

        // Tool chips in flex-wrap row
        const uniqueTools = [...new Set(group.toolNames)];

        return (
          <div key={`tools-${idx}`} className="flex flex-wrap gap-1.5 mt-1 ml-5">
            {uniqueTools.map((name, toolIdx) => (
              <span
                key={name}
                className={cn(
                  "bg-card text-muted-foreground text-xs rounded px-2 py-0.5 border border-border",
                  isActive && toolIdx === uniqueTools.length - 1 && "animate-pulse"
                )}
              >
                {getV2ToolDisplayName(name)}
              </span>
            ))}
          </div>
        );
      })}
    </>
  );
});

// Renders a collapsible subagent section as a bordered card
const SubagentSection = React.memo<{
  item: ThoughtProcessItem;
  shouldBlink: boolean;
  isFirst: boolean;
}>(function SubagentSection({ item, shouldBlink, isFirst }) {
  const [isOpen, setIsOpen] = React.useState(false);
  const subItems: ReadonlyArray<SubagentItemType> = item.subagentItems || [];
  const description = item.subagentDescription || "Subagent";
  const displayText = getDisplayText(item);
  const subagentOutput = item.subagentOutput?.replace(/`?««memory:[a-f0-9-]+»»`?/gi, "");

  // Check if any subagent items have text content worth expanding to
  const hasTextContent = subItems.some(
    (si) => si.thinkingSummary?.trim() || si.text?.trim() || si.thinking?.trim()
  );

  // Collect tool names for summary
  const toolNames = subItems
    .map((si) => si.toolUse?.toolUse?.name)
    .filter((n): n is string => !!n);

  const isExpandable = (hasTextContent || !!subagentOutput || toolNames.length > 0) && subItems.length > 0;
  const isStreaming = shouldBlink && subItems.length === 0;
  const isCompleted = !shouldBlink;

  // Get latest text preview from subagent items for collapsed state
  const latestTextPreview = React.useMemo(() => {
    for (let i = subItems.length - 1; i >= 0; i--) {
      const si = subItems[i];
      const text = si.thinkingSummary?.trim() || si.text?.trim() || si.thinking?.trim();
      if (text) {
        // Truncate to ~80 chars for preview
        return text.length > 80 ? text.substring(0, 80) + "..." : text;
      }
    }
    return null;
  }, [subItems]);

  // Auto-open when subagent is actively streaming (only if there's text to show)
  React.useEffect(() => {
    if (shouldBlink && hasTextContent) {
      setIsOpen(true);
    }
  }, [shouldBlink, hasTextContent]);

  // Subtitle text
  const subtitleText = isStreaming
    ? "Running..."
    : toolNames.length > 0
      ? `using ${toolNames.length} ${toolNames.length === 1 ? "tool" : "tools"}`
      : isCompleted
        ? "completed"
        : null;

  return (
    <div className={cn("flex flex-col gap-0", isFirst ? "mt-2" : "mt-3")}>
      {/* Display text renders OUTSIDE the card */}
      {displayText && (
        <div className="flex gap-2 items-start mb-2">
          <span className={cn("text-muted-foreground flex-shrink-0", shouldBlink && "animate-pulse")}>
            ●
          </span>
          <div className="text-foreground flex-1">
            <Markdown>{displayText}</Markdown>
          </div>
        </div>
      )}

      {/* Bordered card */}
      <div
        className={cn(
          "border rounded-md bg-background p-3",
          shouldBlink ? "border-blue-500/30" : "border-border",
          isExpandable && "cursor-pointer"
        )}
        onClick={isExpandable ? () => setIsOpen(!isOpen) : undefined}
      >
        {/* Header row */}
        <div className="flex items-start gap-2">
          <Bot className={cn("h-4 w-4 text-blue-400 flex-shrink-0 mt-0.5", shouldBlink && "animate-pulse")} />
          <div className="flex-1 min-w-0">
            <div className="text-foreground text-sm font-medium">{description}</div>
            {subtitleText && (
              <div className={cn(
                "text-xs mt-0.5",
                isStreaming ? "text-muted-foreground italic animate-pulse" : "text-muted-foreground"
              )}>
                {subtitleText}
              </div>
            )}
            {/* Text preview in collapsed state */}
            {!isOpen && latestTextPreview && (
              <div className="text-xs text-muted-foreground mt-1 line-clamp-1">
                {latestTextPreview}
              </div>
            )}
          </div>
          {isExpandable && (
            <ChevronDown
              className={cn(
                "h-4 w-4 text-muted-foreground flex-shrink-0 transition-transform mt-0.5",
                !isOpen && "-rotate-90"
              )}
            />
          )}
        </div>

        {/* Expanded content */}
        {isOpen && isExpandable && (
          <div className="border-t border-border mt-2 pt-2">
            <SubagentItemList items={subItems} shouldBlink={shouldBlink} />
            {/* Subagent final output */}
            {subagentOutput && (
              <div className="border-t border-border mt-2 pt-2">
                <div className="text-xs text-muted-foreground mb-1">Result</div>
                <div className="text-sm text-foreground">
                  <Markdown>{subagentOutput}</Markdown>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
});

export const V2AgentThoughtProcessRenderer: React.FC<V2AgentThoughtProcessRendererProps> = ({
  items,
  isThinking = false,
  startedAt,
  endedAt,
  onToolClick,
  pendingToolUses = [],
  onApproveToolUse,
  onRejectToolUse,
  approvalInFlight = false,
  hasTimedOutQuestion = false,
  pendingQuestions = [],
  onQuestionSubmit,
  isSubmittingQuestion = false,
}) => {
  // Get a deterministic thinking message based on current items length
  // This avoids hydration mismatches while still varying the message with conversation state
  const thinkingMessage = React.useMemo(() => {
    const seed = items?.length ?? 0;
    const index = seed % THINKING_MESSAGES.length;
    return THINKING_MESSAGES[index];
  }, [items?.length]);

  // Group items - collect consecutive tools together for potential clustering
  const renderGroups = React.useMemo(() => {
    const groups: Array<{
      type: "text" | "tool-group" | "thinking-summary" | "subagent";
      indices: number[];
    }> = [];

    if (!items || items.length === 0) {
      return groups;
    }

    let i = 0;
    while (i < items.length) {
      const item = items[i];

      // Subagent block - Task/Agent tool (detect by name, not by subagentItems presence)
      // Claude Code v2.1.63+ renamed "Task" to "Agent"
      if (item.toolUse?.toolUse?.name === "Task" || item.toolUse?.toolUse?.name === "Agent") {
        groups.push({
          type: "subagent",
          indices: [i],
        });
        i++;
        continue;
      }

      // Text-only block (no tool)
      if ((item.text || item.thinkingSummary) && !item.toolUse) {
        groups.push({
          type: item.thinkingSummary && !item.text ? "thinking-summary" : "text",
          indices: [i],
        });
        i++;
        continue;
      }

      // Tool block - collect consecutive tools together
      if (item.toolUse) {
        const toolIndices = [i];
        i++;

        // Look ahead for more consecutive tools (without text in between)
        while (i < items.length) {
          const nextItem = items[i];

          // Stop if there's text without a tool
          if ((nextItem.text || nextItem.thinkingSummary) && !nextItem.toolUse) {
            break;
          }

          // Stop if next item is a subagent block
          if (nextItem.toolUse?.toolUse?.name === "Task" || nextItem.toolUse?.toolUse?.name === "Agent") {
            break;
          }

          // Collect this tool
          if (nextItem.toolUse) {
            toolIndices.push(i);
            i++;
          } else {
            break;
          }
        }

        groups.push({
          type: "tool-group",
          indices: toolIndices,
        });
        continue;
      }

      i++;
    }

    return groups;
  }, [items]);

  // Find the last visible group for blinking animation
  const lastVisibleGroupIndex = renderGroups.length - 1;

  // Show loading state if thinking but no items yet
  const showLoadingState = isThinking && (!items || items.length === 0 || renderGroups.length === 0);

  // Track accordion open/closed state
  const [accordionValue, setAccordionValue] = React.useState<string>("");

  // Track elapsed time during thinking
  const [elapsedSeconds, setElapsedSeconds] = React.useState(0);

  // Auto-open when thinking starts, auto-close when thinking ends
  React.useEffect(() => {
    if (isThinking) {
      setAccordionValue("agent-thought-process");
    } else {
      setAccordionValue("");
    }
  }, [isThinking]);

  // Calculate elapsed seconds from startedAt to now (for in-progress thinking)
  const calculateLiveElapsedSeconds = React.useCallback(() => {
    if (!startedAt) return 0;
    const startTime = new Date(startedAt).getTime();
    const now = Date.now();
    return Math.max(0, Math.floor((now - startTime) / 1000));
  }, [startedAt]);

  // Calculate total elapsed seconds from startedAt to endedAt (for completed thinking)
  const calculateTotalElapsedSeconds = React.useCallback(() => {
    if (!startedAt || !endedAt) return 0;
    const startTime = new Date(startedAt).getTime();
    const endTime = new Date(endedAt).getTime();
    return Math.max(0, Math.floor((endTime - startTime) / 1000));
  }, [startedAt, endedAt]);

  // Timer to track elapsed seconds during thinking
  React.useEffect(() => {
    if (isThinking) {
      // Calculate elapsed time from startedAt (persists across refresh)
      setElapsedSeconds(calculateLiveElapsedSeconds());

      const interval = setInterval(() => {
        setElapsedSeconds(calculateLiveElapsedSeconds());
      }, 1000);

      return () => clearInterval(interval);
    } else if (startedAt && endedAt) {
      // When thinking is complete, show the total duration (endedAt - startedAt)
      setElapsedSeconds(calculateTotalElapsedSeconds());
    }
    return undefined;
  }, [isThinking, calculateLiveElapsedSeconds, calculateTotalElapsedSeconds, startedAt, endedAt]);

  return (
    <Accordion type="single" collapsible value={accordionValue} onValueChange={setAccordionValue}>
      <AccordionItem value="agent-thought-process" className="border-none relative overflow-hidden rounded-md">
        {isThinking && (
          <BorderBeam
            size={220}
            duration={5}
            colorFrom="#888888"
            colorTo="hsl(var(--border))"
            borderWidth={1}
          />
        )}
        <AccordionTrigger className="px-3 py-2 border border-border rounded-md bg-popover hover:bg-secondary transition-colors hover:no-underline">
          <div className="flex items-center gap-2">
            {isThinking && <Loader2 className="h-3 w-3 animate-spin text-muted-foreground" />}
            <span className="text-xs text-muted-foreground">
              Agent Thought Process
              {isThinking && elapsedSeconds > 0 && (
                <span className="text-muted-foreground"> (thought for {elapsedSeconds}s...)</span>
              )}
              {!isThinking && elapsedSeconds > 0 && (
                <span className="text-muted-foreground"> (thought for {elapsedSeconds}s)</span>
              )}
            </span>
          </div>
        </AccordionTrigger>
        <AccordionContent className="border-l border-r border-b rounded-b border-border bg-popover">
          <div className="flex flex-col gap-0 px-3">
            {showLoadingState ? (
              <div className="flex gap-2 items-start">
                <span className="text-muted-foreground flex-shrink-0 animate-pulse">●</span>
                <span className="text-foreground animate-pulse">{thinkingMessage}</span>
              </div>
            ) : (
              renderGroups.map((group, groupIndex) => {
                const isLastGroup = groupIndex === lastVisibleGroupIndex;
                const shouldBlink = isLastGroup && isThinking;

                // Thinking summary block
                if (group.type === "thinking-summary") {
                const item = items[group.indices[0]];
                const summaryText = transformTextForTimeout(item.thinkingSummary || "", hasTimedOutQuestion);
                return (
                  <div key={`thinking-${groupIndex}`} className={cn("flex gap-2 items-start", groupIndex === 0 ? "mt-2" : "mt-3")}>
                    <span className={cn("text-muted-foreground flex-shrink-0", shouldBlink && "animate-pulse")}>
                      ●
                    </span>
                    <div className="text-foreground flex-1">
                      <Markdown>{summaryText || ""}</Markdown>
                    </div>
                  </div>
                );
              }

              // Text block
              if (group.type === "text") {
                const item = items[group.indices[0]];
                const displayText = transformTextForTimeout(getDisplayText(item), hasTimedOutQuestion);
                if (!displayText) return null;
                return (
                  <div key={`text-${groupIndex}`} className={cn("flex gap-2 items-start", groupIndex === 0 ? "mt-2" : "mt-3")}>
                    <span className={cn("text-muted-foreground flex-shrink-0", shouldBlink && "animate-pulse")}>
                      ●
                    </span>
                    <div className="text-foreground flex-1">
                      <Markdown>{displayText}</Markdown>
                    </div>
                  </div>
                );
              }

              // Subagent section (Task tool with nested items)
              if (group.type === "subagent") {
                const subagentItem = items[group.indices[0]];
                return (
                  <SubagentSection
                    key={`subagent-${groupIndex}`}
                    item={subagentItem}
                    shouldBlink={shouldBlink}
                    isFirst={groupIndex === 0}
                  />
                );
              }

              // Tool group (may be clustered or individual tools)
              if (group.type === "tool-group") {
                return (
                  <ToolGroupRenderer
                    key={`tool-group-${groupIndex}`}
                    items={items}
                    indices={group.indices}
                    shouldBlink={shouldBlink}
                    isFirstInRenderGroups={groupIndex === 0}
                    onToolClick={onToolClick}
                    pendingToolUses={pendingToolUses}
                    onApproveToolUse={onApproveToolUse}
                    onRejectToolUse={onRejectToolUse}
                    approvalInFlight={approvalInFlight}
                    pendingQuestions={pendingQuestions}
                    onQuestionSubmit={onQuestionSubmit}
                    isSubmittingQuestion={isSubmittingQuestion}
                  />
                );
              }

              return null;
              })
            )}
          </div>
        </AccordionContent>
      </AccordionItem>
    </Accordion>
  );
};
